import { Milestone } from "../types/service.type";
import { ContractState } from "../types/contractState.types";
import { MilestoneStatusEnum, ServiceContractStatusEnum } from "../enum/service.enum";

import { isContractPaid } from "./contractBusinessLogic";

/**
 * Determines if a requester can release payment for a milestone.
 * Conditions:
 * - User is NOT the provider (is requester)
 * - Milestone is NOT paid
 * - Milestone status is Pinning
 * - Contract is NOT canceled after payment
 */
export const canRequesterReleasePayment = (
  milestone: Milestone,
  contractStatus: ServiceContractStatusEnum,
  isProvider: boolean,
  isOwnershipLoading: boolean
): boolean => {
  // Only requesters can release payment
  if (isProvider || isOwnershipLoading) {
    return false;
  }

  // Milestone must be in pinning status and not paid
  if (milestone.status !== MilestoneStatusEnum.Pinning) {
    return false;
  }

  // Cannot release if contract was canceled after payment
  if (contractStatus === ServiceContractStatusEnum.CanceledAfterPaid) {
    return false;
  }

  return true;
};

/**
 * Determines if a provider can send a payment reminder for a milestone.
 * Conditions:
 * - User IS the provider
 * - Contract payment is complete
 * - Milestone status is Pinning
 * - Contract is NOT canceled after payment
 */
export const canProviderSendReminder = (
  milestone: Milestone,
  contractState: ContractState,
  isProvider: boolean,
  isOwnershipLoading: boolean
): boolean => {
  // Only providers can send reminders
  if (!isProvider || isOwnershipLoading) {
    return false;
  }

  // Contract must be paid
  if (!isContractPaid(contractState)) {
    return false;
  }

  // Milestone must be in pinning status
  if (milestone.status !== MilestoneStatusEnum.Pinning) {
    return false;
  }

  // Cannot send reminder if contract was canceled after payment
  if (contractState.contractStatus === ServiceContractStatusEnum.CanceledAfterPaid) {
    return false;
  }

  return true;
};

/**
 * Determines if milestone status should be displayed.
 * Rules differ for provider vs requester:
 * - Provider: Show if contract was canceled after payment OR
 *             (contract is paid AND milestone is not in Coming status)
 * - Requester: Show if contract is paid
 */
export const shouldShowMilestoneStatus = (
  milestone: Milestone,
  contractState: ContractState,
  isProvider: boolean
): boolean => {
  const contractPaid = isContractPaid(contractState);

  if (isProvider) {
    // Provider sees status if:
    // 1. Contract was canceled after payment, OR
    // 2. Contract is paid AND milestone is not coming
    return (
      contractState.contractStatus === ServiceContractStatusEnum.CanceledAfterPaid ||
      (contractPaid && milestone.status !== MilestoneStatusEnum.Coming)
    );
  }

  // Requester sees status if contract is paid
  return contractPaid;
};

/**
 * Type representing available milestone actions
 */
export type MilestoneActionType = "release" | "remind" | null;

/**
 * Determines which action (if any) is available for a milestone.
 * Returns 'release' for requester, 'remind' for provider, or null if no action available.
 */
export const getMilestoneActionType = (
  milestone: Milestone,
  contractState: ContractState,
  isProvider: boolean,
  isOwnershipLoading: boolean
): MilestoneActionType => {
  if (canRequesterReleasePayment(milestone, contractState.contractStatus, isProvider, isOwnershipLoading)) {
    return "release";
  }

  if (canProviderSendReminder(milestone, contractState, isProvider, isOwnershipLoading)) {
    return "remind";
  }

  return null;
};
